
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useTeacherProfile } from '@/hooks/useTeacherProfile';

interface PersonalInfoFormProps {
  teacherCode: string;
  onDataChange: (data: any, isValid: boolean) => void;
}

export function PersonalInfoForm({ teacherCode, onDataChange }: PersonalInfoFormProps) {
  const { profile, isLoading } = useTeacherProfile(teacherCode);
  const [formData, setFormData] = useState({
    full_name: '',
    nickname: '',
    phone_number: '',
    teaching_experience: '',
    academic_achievements: ''
  });

  useEffect(() => {
    if (profile) {
      const data = {
        full_name: profile.full_name || '',
        nickname: profile.nickname || '',
        phone_number: profile.phone_number || '',
        teaching_experience: profile.teaching_experience || '',
        academic_achievements: profile.academic_achievements || ''
      };
      setFormData(data);
    }
  }, [profile]);

  // Check if required fields are filled
  const isFormValid = formData.full_name.trim() !== '' && 
                      formData.nickname.trim() !== '' && 
                      formData.phone_number.trim() !== '';

  // Notify parent component when data or validity changes
  useEffect(() => {
    onDataChange(formData, isFormValid);
  }, [formData, isFormValid, onDataChange]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="text-center">กำลังโหลดข้อมูล...</div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="full_name">ชื่อ-นามสกุล *</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              placeholder="กรอกชื่อ-นามสกุลของคุณ"
              className={!formData.full_name.trim() ? 'border-red-300' : ''}
            />
          </div>

          <div>
            <Label htmlFor="nickname">ชื่อเล่น *</Label>
            <Input
              id="nickname"
              value={formData.nickname}
              onChange={(e) => handleChange('nickname', e.target.value)}
              placeholder="กรอกชื่อเล่นของคุณ"
              className={!formData.nickname.trim() ? 'border-red-300' : ''}
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="phone_number">หมายเลขโทรศัพท์ *</Label>
            <Input
              id="phone_number"
              type="tel"
              value={formData.phone_number}
              onChange={(e) => handleChange('phone_number', e.target.value)}
              placeholder="กรอกหมายเลขโทรศัพท์"
              className={!formData.phone_number.trim() ? 'border-red-300' : ''}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="teaching_experience">ประสบการณ์สอน</Label>
          <Textarea
            id="teaching_experience"
            value={formData.teaching_experience}
            onChange={(e) => handleChange('teaching_experience', e.target.value)}
            placeholder="กรอกประสบการณ์การสอนของคุณ"
            rows={3}
          />
        </div>

        <div>
          <Label htmlFor="academic_achievements">ผลงานทางวิชาการ</Label>
          <Textarea
            id="academic_achievements"
            value={formData.academic_achievements}
            onChange={(e) => handleChange('academic_achievements', e.target.value)}
            placeholder="กรอกผลงานทางวิชาการของคุณ"
            rows={3}
          />
        </div>

        {!isFormValid && (
          <div className="text-red-600 text-sm">
            กรุณากรอกข้อมูลที่มีเครื่องหมาย * ให้ครบถ้วน
          </div>
        )}
      </div>
    </Card>
  );
}
